import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./newPurchaseInvoice.css";
import Select from "react-select";
import { useProductContext } from "../../context/ProductContext";
import { useSupplierContext } from "../../context/SupplierContext";
import { usePurchaseInvoiceContext } from "../../context/PurchaseInvoiceContext";

const NewPurchaseInvoice = () => {
  const navigate = useNavigate();
  const { fetchProducts, products, getProductByBarcode } = useProductContext();
  const { fetchSuppliers, suppliers } = useSupplierContext();
  const { createPurchaseInvoice, loading, error, success } =
    usePurchaseInvoiceContext();

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [notes, setNotes] = useState("");
  // تاريخ الفاتورة
  const [createdAt, setCreatedAt] = useState(() => {
    const today = new Date();
    return today.toISOString().slice(0, 10); // yyyy-mm-dd
  });
  const [currentItem, setCurrentItem] = useState({
    product: null,
    quantity: "",
    purchase_price: "",
  });

  // للباركود
  const [barcodeInput, setBarcodeInput] = useState("");
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const typingTimerRef = useRef(null);
  const barcodeInputRef = useRef(null);

  useEffect(() => {
    fetchProducts();
    fetchSuppliers();
  }, []);

  // ✅ معالجة الباركود تلقائياً
  useEffect(() => {
    if (!barcodeInput.trim()) return;

    if (typingTimerRef.current) {
      clearTimeout(typingTimerRef.current);
    }

    typingTimerRef.current = setTimeout(() => {
      handleBarcodeSubmit(barcodeInput.trim());
    }, 150);

    return () => {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current);
      }
    };
  }, [barcodeInput]);

  // ✅ إضافة منتج من الباركود
  const handleBarcodeSubmit = async (barcode) => {
    if (!barcode || barcode.length < 2) {
      setBarcodeInput("");
      return;
    }

    setBarcodeLoading(true);

    try {
      const product = await getProductByBarcode(barcode);

      if (!product) {
        alert("❌ المنتج غير موجود في قاعدة البيانات");
        setBarcodeInput("");
        setBarcodeLoading(false);
        return;
      }

      // 🔐 لا يمكن إضافة كمية لصنف يخص فرعاً آخر
      if (product.isMine === false) {
        alert("هذا الصنف يخص فرعاً آخر. أنشئ صنفاً جديداً في فرعك لإضافة كمية له.");
        setBarcodeInput("");
        setBarcodeLoading(false);
        return;
      }

      // التحقق من وجود المنتج في الفاتورة
      const existingItemIndex = invoiceItems.findIndex(
        (item) => item.product_id === product.id
      );

      if (existingItemIndex !== -1) {
        // إذا كان المنتج موجود، زيادة الكمية
        const updatedItems = [...invoiceItems];
        const existingItem = updatedItems[existingItemIndex];
        const newQuantity = existingItem.quantity + 1;

        updatedItems[existingItemIndex] = {
          ...existingItem,
          quantity: newQuantity,
          total: (newQuantity * (existingItem.purchase_price || 0)).toFixed(2),
        };

        setInvoiceItems(updatedItems);
      } else {
        // إضافة المنتج للفاتورة
        const productDetails = products.find((p) => p.id === product.id);

        const newItem = {
          product_id: product.id,
          product_name: product.name,
          quantity: 1,
          purchase_price: "", // ✅ فاضي - المستخدم يدخل السعر يدوياً
          last_sale_price: product.last_sale_price || 0,
          last_purchase_price: product.last_purchase_price || 0,
          shelves: productDetails?.shelves || [],
          shelf_name: productDetails?.shelf_name || "لا يوجد",
          total: "0.00", // ✅ صفر لحد ما يدخل السعر
        };

        setInvoiceItems([...invoiceItems, newItem]);
      }
      setBarcodeInput("");

      setTimeout(() => {
        barcodeInputRef.current?.focus();
      }, 100);
    } catch (err) {
      console.error("❌ خطأ أثناء إضافة المنتج:", err);
      alert("❌ حدث خطأ أثناء إضافة المنتج");
      setBarcodeInput("");
    } finally {
      setBarcodeLoading(false);
    }
  };

  const handleAddItem = () => {
    if (
      !currentItem.product ||
      !currentItem.quantity ||
      !currentItem.purchase_price
    ) {
      alert("يرجى ملء جميع حقول الصنف");
      return;
    }

    // الحصول على تفاصيل المنتج الكاملة
    const productDetails = products.find(
      (p) => p.id === currentItem.product.value
    );

    const newItem = {
      product_id: currentItem.product.value,
      product_name: currentItem.product.label,
      quantity: currentItem.quantity,
      purchase_price: currentItem.purchase_price,
      last_sale_price: productDetails?.last_sale_price || 0,
      last_purchase_price: productDetails?.last_purchase_price || 0,
      shelves: productDetails?.shelves || [],
      shelf_name: productDetails?.shelf_name || "لا يوجد",
      total: (
        parseFloat(currentItem.quantity) *
        parseFloat(currentItem.purchase_price)
      ).toFixed(2),
    };

    setInvoiceItems([...invoiceItems, newItem]);
    setCurrentItem({ product: null, quantity: "", purchase_price: "" });
  };

  const handleRemoveItem = (index) => {
    setInvoiceItems(invoiceItems.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    return invoiceItems
      .reduce((sum, item) => sum + parseFloat(item.total), 0)
      .toFixed(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSupplier) {
      alert("يرجى اختيار المورد");
      return;
    }

    if (invoiceItems.length === 0) {
      alert("يرجى إضافة صنف واحد على الأقل");
      return;
    }

    const invoiceData = {
      supplier_id: selectedSupplier?.value,
      items: invoiceItems,
      total_amount: calculateTotal(),
      notes: notes.trim() || null,
      created_at: createdAt,
    };

    try {
      const response = await createPurchaseInvoice(invoiceData);
      console.log(response);

      if (response.status === "success") {
        // التوجيه إلى صفحة تفاصيل الفاتورة
        navigate(`/purchase-invoice/${response.invoice_id}`);
      }
    } catch (err) {
      console.error("Error creating invoice:", err);
    }
  };

  return (
    <div className="new-purchase-invoice">
      <div className="wrapper">
        <div className="page-header">
          <h1>إنشاء فاتورة شراء جديدة</h1>
        </div>

        <form onSubmit={handleSubmit} className="invoice-form">
          {/* Supplier Selection */}
          <div className="form-section supplier-section">
            <h2>اختيار المورد</h2>
            <div className="form-group">
              <label htmlFor="supplier">المورد *</label>
              <Select
                id="supplier"
                value={selectedSupplier}
                onChange={setSelectedSupplier}
                options={suppliers.map((supplier) => ({
                  value: supplier.id,
                  label: `${supplier.full_name} - ${supplier.phone}`,
                }))}
                placeholder="-- اختر المورد --"
                isSearchable
                isClearable
                noOptionsMessage={() => "لا توجد خيارات"}
                className="react-select-container"
                classNamePrefix="react-select"
                filterOption={(option, inputValue) => {
                  if (!inputValue) return true;
                  const searchWords = inputValue
                    .toLowerCase()
                    .split(" ")
                    .filter((w) => w);
                  const optionText = option.label.toLowerCase();
                  return searchWords.every((word) => optionText.includes(word));
                }}
              />
            </div>
          </div>

          {/* Invoice Date Section */}
          <div className="form-section date-section">
            <h2>تاريخ الفاتورة</h2>
            <div className="form-group">
              <label htmlFor="created_at">تاريخ الإنشاء *</label>
              <input
                type="date"
                id="created_at"
                value={createdAt}
                onChange={(e) => setCreatedAt(e.target.value)}
                required
              />
            </div>
          </div>

          {/* Barcode Section */}
          <div className="form-section barcode-section">
            <h2>إضافة من الباركود (تلقائي)</h2>
            <div className="form-group">
              <label htmlFor="barcode">مسح الباركود</label>
              <input
                ref={barcodeInputRef}
                type="text"
                id="barcode"
                value={barcodeInput}
                onChange={(e) => setBarcodeInput(e.target.value)}
                placeholder="امسح الباركود هنا..."
                disabled={barcodeLoading}
                autoComplete="off"
                style={{
                  padding: "12px",
                  fontSize: "16px",
                  border: "2px solid #3b82f6",
                  borderRadius: "8px",
                  width: "100%",
                }}
              />
              <p
                style={{ fontSize: "14px", color: "#6b7280", marginTop: "8px" }}
              >
                💡 امسح الباركود مباشرة - سيتم إضافة المنتج تلقائياً
              </p>
            </div>
          </div>

          {/* Add Item Section */}
          <div className="form-section add-item-section">
            <h2>أو اختر الصنف يدوياً</h2>
            <div className="item-inputs">
              <div className="form-group">
                <label htmlFor="product">الصنف *</label>
                <Select
                  id="product"
                  value={currentItem.product}
                  onChange={(selected) =>
                    setCurrentItem({
                      ...currentItem,
                      product: selected,
                    })
                  }
                  options={products
                    .filter((product) => product.isMine !== false)
                    .filter(
                      (product) =>
                        !invoiceItems.some(
                          (item) => item.product_id === product.id
                        )
                    )
                    .map((product) => ({
                      value: product.id,
                      label: `${product.name} - ${product.barcode}`,
                    }))}
                  placeholder="-- اختر الصنف --"
                  isSearchable
                  isClearable
                  noOptionsMessage={() => "لا توجد خيارات"}
                  className="react-select-container"
                  classNamePrefix="react-select"
                  filterOption={(option, inputValue) => {
                    if (!inputValue) return true;
                    const searchWords = inputValue
                      .toLowerCase()
                      .split(" ")
                      .filter((w) => w);
                    const optionText = option.label.toLowerCase();
                    return searchWords.every((word) =>
                      optionText.includes(word)
                    );
                  }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="quantity">الكمية *</label>
                <input
                  type="text"
                  id="quantity"
                  value={currentItem.quantity}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9]/g, "");
                    setCurrentItem({ ...currentItem, quantity: value });
                  }}
                  placeholder="أدخل الكمية"
                />
              </div>

              <div className="form-group">
                <label htmlFor="purchase_price">سعر الشراء *</label>
                <input
                  type="text"
                  id="purchase_price"
                  value={currentItem.purchase_price}
                  onChange={(e) => {
                    const value = e.target.value.replace(/[^0-9.]/g, "");
                    setCurrentItem({
                      ...currentItem,
                      purchase_price: value,
                    });
                  }}
                  placeholder="أدخل سعر الشراء"
                />
              </div>

              <button
                type="button"
                className="add-item-btn"
                onClick={handleAddItem}
              >
                إضافة الصنف
              </button>
            </div>
          </div>

          {/* Invoice Items Table */}
          {invoiceItems.length > 0 && (
            <div className="form-section items-table-section">
              <h2>الأصناف المضافة</h2>
              <div className="table-container">
                <table className="invoice-items-table">
                  <thead>
                    <tr>
                      <th>الصنف</th>
                      <th>الرفوف</th>
                      <th>آخر سعر شراء</th>
                      <th>الكمية</th>
                      <th>سعر الشراء</th>
                      <th>المجموع</th>
                      <th>إجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoiceItems.map((item, index) => (
                      <tr key={index}>
                        <td>{item.product_name}</td>
                        <td>
                          <span
                            style={{
                              color:
                                item.shelf_name === "لا يوجد"
                                  ? "#ef4444"
                                  : "#059669",
                              fontWeight: "600",
                            }}
                          >
                            {item.shelf_name}
                          </span>
                        </td>
                        <td>
                          <span style={{ color: "#3b82f6", fontWeight: "600" }}>
                            {parseFloat(item.last_purchase_price || 0).toFixed(
                              2
                            )}{" "}
                            ₪
                          </span>
                        </td>
                        <td>
                          <input
                            type="text"
                            value={item.quantity}
                            onChange={(e) => {
                              const value = e.target.value.replace(
                                /[^0-9]/g,
                                ""
                              );
                              const newQty = parseInt(value) || 1;
                              const updatedItems = [...invoiceItems];
                              updatedItems[index].quantity = newQty;
                              updatedItems[index].total = (
                                newQty *
                                parseFloat(
                                  updatedItems[index].purchase_price || 0
                                )
                              ).toFixed(2);
                              setInvoiceItems(updatedItems);
                            }}
                            style={{
                              width: "80px",
                              padding: "6px",
                              textAlign: "center",
                              border: "1px solid #d1d5db",
                              borderRadius: "4px",
                            }}
                          />
                        </td>
                        <td>
                          <input
                            type="text"
                            value={item.purchase_price || ""}
                            required
                            onChange={(e) => {
                              const value = e.target.value.replace(
                                /[^0-9.]/g,
                                ""
                              );
                              const newPrice = parseFloat(value) || 0;
                              const updatedItems = [...invoiceItems];
                              updatedItems[index].purchase_price = value;
                              updatedItems[index].total = (
                                updatedItems[index].quantity * newPrice
                              ).toFixed(2);
                              setInvoiceItems(updatedItems);
                            }}
                            placeholder="أدخل السعر"
                            style={{
                              width: "100px",
                              padding: "6px",
                              textAlign: "center",
                              border: "1px solid #d1d5db",
                              borderRadius: "4px",
                            }}
                          />
                        </td>
                        <td>
                          <span style={{ fontWeight: "700", color: "#059669" }}>
                            {item.total} ₪
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="remove-btn"
                            onClick={() => handleRemoveItem(index)}
                          >
                            حذف
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="5" className="total-label">
                        المجموع الكلي:
                      </td>
                      <td className="total-amount">{calculateTotal()} ₪</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          )}

          {/* Notes Section */}
          <div className="form-section notes-section">
            <h2>ملاحظات (اختياري)</h2>
            <div className="form-group">
              <textarea
                id="notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="أدخل أي ملاحظات إضافية..."
                rows="4"
                className="notes-textarea"
              />
            </div>
          </div>

          {/* Messages */}
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          {/* Submit Button */}
          <div className="form-actions">
            <button
              type="submit"
              className="submit-btn"
              disabled={loading || invoiceItems.length === 0}
            >
              {loading ? "جاري الحفظ..." : "حفظ الفاتورة"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewPurchaseInvoice;
